import { Form, FormikProvider, useFormik } from 'formik';
import MessageFormProps, { upsertMessageValidationSchema } from './dto';
import {
    Box,
    CardHeader,
    Divider,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Chip,
    Paper,
    Tooltip,
    IconButton,
} from '@mui/material';
import { getMessageFormInitialValues } from './utils';
import { useNavigate } from 'react-router';
import { MessageTypeOptions, MessageStatus, MessageType } from 'common/dto-types/message-dto';
import AsyncSelectSearchFormField from 'components/async-select-search-form-field';
import FDCPServices from 'fdcp/fdcp-services';
import TournamentDto, { mapFdcpSeasonToTournamentDto } from 'common/dto-types/tournament-dto';
import ReactQuillWrapper from 'components/react-quill-wrapper/react-quill-wrapper';
import MultiTableDialogSelector from 'components/multi-table-dialog-selector';
import SelectUserTableColumnConfig, {
    DefaultVisibleUserColumns,
    SearchableUserTableColumnConfig,
} from './recipient-select-column-config';
import { fetchActiveUserForMessaging } from 'pages/user-list-page/api';
import { InfoCircleOutlined } from '@ant-design/icons';
import MessageTargetPreviewTable from './message-target-preview-table';
import MessageFormActionButton from './message-form-action-button';
import { useMessageActions } from 'hooks/useMessageActions';
import ContentfulArticleSearchField from 'components/contentful-article-search-field';
import { ArticlePreviewDto } from 'components/contentful-article-search-field/dto';
import MessageGroupSelection from './message-group-selection';
import { useState, useEffect, useRef } from 'react';
import { READ_ONLY_FIELD_STYLES } from 'utils/field-styles';
import UserGroupDto from 'common/dto-types/user-group-dto';
import axiosServices from 'utils/axios';

const MessageForm = ({ message }: MessageFormProps) => {
    const initialValues = getMessageFormInitialValues(message);
    const navigate = useNavigate();
    const [articlePreview, setArticlePreview] = useState<ArticlePreviewDto | null>(null);
    const [validatedRestrictedGroups, setValidatedRestrictedGroups] = useState<UserGroupDto[]>([]);
    const isInitialMount = useRef(true);
    const hasLoadedInitialArticle = useRef(false);

    const isReadOnly = message?.status !== MessageStatus.Draft && message?.status !== undefined;

    // Determine form title based on message status
    const getFormTitle = () => {
        if (!message) {
            return 'Create Message';
        }
        if (message.status === MessageStatus.Draft) {
            return 'Edit Message';
        }
        return 'View Message';
    };

    const formik = useFormik({
        initialValues,
        validationSchema: upsertMessageValidationSchema,
        enableReinitialize: true,
        onSubmit: async () => {
            // onSubmit is handled by the hook actions
        },
    });

    const { isSubmitting, values, errors, touched, isValid, getFieldProps } = formik;

    // Calculate if basic validation passes (for save operations)
    // Basic validation checks: messageTitle, messageDescription, messageType, and promotionMessagePayload.description
    // It allows empty articleMessagePayload.articleEntryId and userGroups
    const hasBasicErrors = Object.keys(errors).some((key) => {
        // Fields that are part of basic validation (always required)
        const basicFields = ['messageTitle', 'messageDescription', 'messageType'];

        // Check top-level fields
        if (basicFields.includes(key)) {
            return true;
        }

        // Check nested promotionMessagePayload.description
        if (key === 'promotionMessagePayload' && errors.promotionMessagePayload) {
            const promotionErrors = errors.promotionMessagePayload as any;
            return Boolean(promotionErrors.description);
        }

        // Check nested articleMessagePayload.articleEntryId validation
        if (key === 'articleMessagePayload' && errors.articleMessagePayload) {
            const articleErrors = errors.articleMessagePayload as any;
            return Boolean(articleErrors.articleEntryId);
        }

        return false;
    });
    const isBasicValid = !hasBasicErrors;

    // Use the message actions hook
    const messageActions = useMessageActions({
        message,
        formik,
        navigate,
    });

    const handleRemoveUser = (userId: number) => {
        const updatedUsers = values.selectedUsers.filter((user) => user.id !== userId);
        formik.setFieldValue('selectedUsers', updatedUsers);
    };

    // Action handlers
    const handleCancel = () => {
        navigate(-1);
    };

    const handleCreate = async () => {
        await messageActions.onCreate();
    };

    const handleUpdate = async () => {
        await messageActions.onUpdate();
    };

    const handleUpdateWithoutExit = async () => {
        await messageActions.onUpdate(false);
    };

    const handleSendTest = async () => {
        await messageActions.onSendTest();
    };

    const handlePublish = async () => {
        await messageActions.onPublish();
    };

    const handleDuplicate = async () => {
        await messageActions.onDuplicate();
    };

    const handleDelete = async () => {
        await messageActions.onDelete();
    };

    const handleRecall = async () => {
        await messageActions.onRecall();
    };

    // Fetch validated restricted groups when article preview changes
    useEffect(() => {
        const isArticleType = values.messageType === MessageType.Article;
        const hasArticleGroups = articlePreview?.userGroups && articlePreview.userGroups.length > 0;

        if (!isArticleType || !hasArticleGroups) {
            setValidatedRestrictedGroups([]);
            return;
        }

        // Build MultiNamesSpecification with all group names from article
        const groupNames = articlePreview.userGroups.map((g) => g.name).join(';');
        const queryParam = `?andSpecifications=MultiNamesSpecification:${groupNames}`;

        axiosServices
            .get(`userGroups${queryParam}`)
            .then((res) => {
                setValidatedRestrictedGroups(res.data.elements);
            })
            .catch((error) => {
                console.error('Failed to fetch restricted user groups:', error);
                setValidatedRestrictedGroups([]);
            });
    }, [values.messageType, articlePreview]);

    // Reset user selection when entering or exiting restricted mode
    // Only clear selections when NOT in read-only mode (to preserve archived message data)
    useEffect(() => {
        // Skip on initial mount to preserve selectedUsers from initialValues
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        // Skip when article preview is loaded for the first time (auto-fetch on page load)
        // This preserves selectedUsers from the existing message
        if (articlePreview && !hasLoadedInitialArticle.current && message?.articleMessagePayload?.articleEntryId) {
            hasLoadedInitialArticle.current = true;
            return;
        }

        if (isReadOnly) {
            // Don't clear selections for archived messages - preserve historical data
            return;
        }

        if (values.selectedUsers.length > 0) {
            const isArticleType = values.messageType === MessageType.Article;
            const hasArticleGroups = articlePreview?.userGroups && articlePreview.userGroups.length > 0;
            const isRestrictedMode = isArticleType && hasArticleGroups;

            // Clear user selection when article with user groups is selected (entering restricted mode)
            // or when article is changed/removed (exiting restricted mode)
            if (isRestrictedMode || (!isRestrictedMode && validatedRestrictedGroups.length === 0)) {
                formik.setFieldValue('selectedUsers', []);
            }
        }
    }, [articlePreview, values.messageType, isReadOnly]);

    return (
        <FormikProvider value={formik}>
            <Box sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CardHeader title={getFormTitle()} sx={{ p: 0 }} />
                        {isReadOnly && (
                            <Tooltip
                                title={<Typography variant="body2">This message is in read-only mode.</Typography>}
                                arrow
                                placement="right"
                            >
                                <IconButton size="small" sx={{ color: 'text.disabled' }}>
                                    <InfoCircleOutlined style={{ fontSize: '1.1rem' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                    <MessageFormActionButton
                        message={message}
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        isBasicValid={isBasicValid}
                        onCancel={handleCancel}
                        onCreate={handleCreate}
                        onUpdate={handleUpdate}
                        onUpdateWithoutExit={handleUpdateWithoutExit}
                        onSendTest={handleSendTest}
                        onPublish={handlePublish}
                        onDuplicate={handleDuplicate}
                        onDelete={handleDelete}
                        onRecall={handleRecall}
                        placement="top"
                    />
                </Stack>
            </Box>
            <Divider />
            <Form>
                <CardHeader title="Basic Information" />
                <Divider />
                <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="message-title">Message Title *</InputLabel>
                                <TextField
                                    fullWidth
                                    id="message-title"
                                    placeholder="Enter Message Title"
                                    disabled={isReadOnly}
                                    InputProps={{
                                        sx: isReadOnly ? READ_ONLY_FIELD_STYLES : undefined,
                                    }}
                                    error={Boolean(
                                        (touched.messageTitle || values.messageTitle) && errors.messageTitle,
                                    )}
                                    {...getFieldProps('messageTitle')}
                                />
                                {(touched.messageTitle || values.messageTitle) && errors.messageTitle && (
                                    <FormHelperText error id="message-title-helper">
                                        {errors.messageTitle}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="message-description">Message Description</InputLabel>
                                <TextField
                                    fullWidth
                                    id="message-description"
                                    placeholder="Enter Message Description"
                                    disabled={isReadOnly}
                                    InputProps={{
                                        sx: isReadOnly ? READ_ONLY_FIELD_STYLES : undefined,
                                    }}
                                    error={Boolean(
                                        (touched.messageDescription || values.messageDescription) &&
                                            errors.messageDescription,
                                    )}
                                    {...getFieldProps('messageDescription')}
                                />
                                {(touched.messageDescription || values.messageDescription) &&
                                    errors.messageDescription && (
                                        <FormHelperText error id="message-description-helper">
                                            {errors.messageDescription}
                                        </FormHelperText>
                                    )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <AsyncSelectSearchFormField<TournamentDto>
                                    label="Tournament"
                                    name="tournament"
                                    initialValues={message?.tournament || null}
                                    disabled={isReadOnly}
                                    fetchData={(inputValue) =>
                                        FDCPServices.searchSeasonByName({ searchString: inputValue }).then((seasons) =>
                                            seasons.map((season) => mapFdcpSeasonToTournamentDto(season)),
                                        )
                                    }
                                    getOptionLabel={(option) => {
                                        if (!option?.name) return '';
                                        return `${option.name} - [${option.fdcpId ?? ''}]`;
                                    }}
                                    isOptionEqualToValue={(option, selectedValue) => {
                                        return option.fdcpId === selectedValue.fdcpId;
                                    }}
                                    filterOptions={(x) => x}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack spacing={1.25}>
                                <InputLabel htmlFor="message-type">Message Type *</InputLabel>
                                <Select
                                    fullWidth
                                    id="message-type"
                                    displayEmpty
                                    disabled={isReadOnly}
                                    sx={isReadOnly ? READ_ONLY_FIELD_STYLES : undefined}
                                    error={Boolean((touched.messageType || values.messageType) && errors.messageType)}
                                    {...getFieldProps('messageType')}
                                >
                                    <MenuItem value="" disabled>
                                        <em>Choose one option</em>
                                    </MenuItem>
                                    {MessageTypeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {(touched.messageType || values.messageType) && errors.messageType && (
                                    <FormHelperText error id="message-type-helper">
                                        {errors.messageType}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                <CardHeader title="Message Content" />
                <Divider />
                <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                        {!values.messageType && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                                    Please select a message type to continue
                                </Typography>
                            </Grid>
                        )}
                        {values.messageType === MessageType.Promotion && (
                            <Grid item xs={12}>
                                <Stack spacing={1.25}>
                                    <InputLabel htmlFor="promotion-description">Promotion Description *</InputLabel>
                                    <Box>
                                        <ReactQuillWrapper
                                            name="promotionMessagePayload.description"
                                            minHeight="200px"
                                            maxLength={2000}
                                            readOnly={isReadOnly}
                                        />
                                    </Box>
                                </Stack>
                            </Grid>
                        )}
                        {values.messageType === MessageType.Article && (
                            <Grid item xs={12}>
                                <ContentfulArticleSearchField
                                    name="articleMessagePayload.articleEntryId"
                                    label="Article Entry ID"
                                    value={values.articleMessagePayload?.articleEntryId || ''}
                                    disabled={isReadOnly}
                                    onChange={(value) =>
                                        formik.setFieldValue('articleMessagePayload.articleEntryId', value)
                                    }
                                    onBlur={formik.handleBlur}
                                    onValidationStatusChange={(status) =>
                                        formik.setFieldValue('articleMessagePayload.validationStatus', status)
                                    }
                                    onArticlePreviewChange={(preview) => setArticlePreview(preview)}
                                    error={Boolean(
                                        (touched.articleMessagePayload as any)?.articleEntryId &&
                                            (errors.articleMessagePayload as any)?.articleEntryId,
                                    )}
                                    helperText={
                                        (touched.articleMessagePayload as any)?.articleEntryId &&
                                        (errors.articleMessagePayload as any)?.articleEntryId
                                            ? String((errors.articleMessagePayload as any).articleEntryId)
                                            : ''
                                    }
                                    isRequired
                                />
                            </Grid>
                        )}
                    </Grid>
                </Box>

                <CardHeader
                    title={
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="h5">Message Targets</Typography>
                                {!isReadOnly && (
                                    <Tooltip
                                        title={
                                            <Typography variant="body2" component="div">
                                                <strong>
                                                    Please prioritize choosing message recipients in the following
                                                    order:
                                                </strong>
                                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                                    <li>
                                                        <strong>User Groups:</strong> Select groups to send message to
                                                        all members
                                                    </li>
                                                    <li>
                                                        <strong>Add Specific Users:</strong> You can specify individual
                                                        users to send to
                                                    </li>
                                                </ul>
                                                <br />
                                                <strong>Click preview button to see final recipient list</strong>
                                            </Typography>
                                        }
                                        arrow
                                        placement="right"
                                    >
                                        <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                            <InfoCircleOutlined style={{ fontSize: '1rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                            <MessageTargetPreviewTable
                                selectedUsers={values.selectedUsers}
                                userGroups={values.userGroups}
                            />
                        </Stack>
                    }
                />
                <Divider />
                <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Stack spacing={1.25}>
                                <MessageGroupSelection
                                    label="User Groups"
                                    name="userGroups"
                                    initialValues={message?.messageTarget?.groups || []}
                                    restrictedGroups={validatedRestrictedGroups}
                                    disabled={isReadOnly}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                {!isReadOnly && (
                                    <MultiTableDialogSelector
                                        name="selectedUsers"
                                        buttonLabel="Add Specific Users"
                                        dialogTitle={
                                            validatedRestrictedGroups.length > 0
                                                ? 'Select Users (restricted by user groups of selected article)'
                                                : 'Select Users'
                                        }
                                        defaultVisibleColumnConfig={DefaultVisibleUserColumns}
                                        defaultTableColumnConfig={SelectUserTableColumnConfig}
                                        searchableTableColumnConfig={SearchableUserTableColumnConfig}
                                        fetchDataCb={(params) => {
                                            const groupIds =
                                                validatedRestrictedGroups.length > 0
                                                    ? validatedRestrictedGroups.map((g) => g.id)
                                                    : undefined;
                                            return fetchActiveUserForMessaging(params, groupIds);
                                        }}
                                        idAccessor="id"
                                    />
                                )}
                                {values.selectedUsers && values.selectedUsers.length > 0 && (
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            flex: 1,
                                            p: 2,
                                            maxHeight: 200,
                                            overflowY: 'auto',
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                            Selected Users ({values.selectedUsers.length})
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {values.selectedUsers.map((user) => (
                                                <Chip
                                                    key={user.id}
                                                    label={`${user.trigram} / ${user.email}`}
                                                    onDelete={isReadOnly ? undefined : () => handleRemoveUser(user.id)}
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ mb: 1 }}
                                                />
                                            ))}
                                        </Stack>
                                    </Paper>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
                <Divider />
                <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                    <MessageFormActionButton
                        message={message}
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        isBasicValid={isBasicValid}
                        onCancel={handleCancel}
                        onCreate={handleCreate}
                        onUpdate={handleUpdate}
                        onUpdateWithoutExit={handleUpdateWithoutExit}
                        onSendTest={handleSendTest}
                        onPublish={handlePublish}
                        onDuplicate={handleDuplicate}
                        onDelete={handleDelete}
                        onRecall={handleRecall}
                        placement="bottom"
                    />
                </Box>
            </Form>
        </FormikProvider>
    );
};

export default MessageForm;
